const { Binary } = require('binary');
const util = require('util');
const CombinedStream = require('combined-stream');
const { ReadableStreamBuffer } = require('stream-buffers');
const utils = require('../utils');
const Algorithms = require('./algorithms');
const SymmetricCipherBase = require('./symmetric-cipher-base');

/**
 *
 * @constructor
 */
function AesGcmCipher(cryptoService) {
  AesGcmCipher.super_.call(this, cryptoService);
  this.name = 'AES-256-GCM';
  this.ivLength = AesGcmCipher.IV_LENGTH;
  this.keyLength = AesGcmCipher.KEY_LENGTH;
}

util.inherits(AesGcmCipher, SymmetricCipherBase);

AesGcmCipher.KEY_LENGTH = 32;
AesGcmCipher.IV_LENGTH = 12;

/**
 * Factory function for {AesGcmCipher} objects
 * @returns {AesGcmCipher}
 */
AesGcmCipher.create = function (cryptoService) {
  return new AesGcmCipher(cryptoService);
};

/**
 * Encrypts the payload using AES w/ GCM mode.  This function will take the
 * result from the crypto service and construct the payload automatically from
 * it's parts.  There is no need to process the payload.
 * @returns {Object}
 */
AesGcmCipher.prototype.encrypt = function (payload, key, iv) {
  return this.cryptoService
    .encrypt(payload, key, iv, Algorithms.AES_256_GCM)
    .then(function resultHandler(result) {
      const ivBuffer = iv.asBuffer();
      const authTagBuffer = result.authTag.asBuffer();

      if (utils.isStream(result.payload)) {
        const ivStream = new ReadableStreamBuffer();
        const authTagStream = new ReadableStreamBuffer();

        ivStream.put(ivBuffer);
        authTagStream.put(authTagBuffer);

        const combinedStream = CombinedStream.create({ maxDataSize: Infinity });
        combinedStream.append(ivStream);
        combinedStream.append(result.payload);
        combinedStream.append(authTagStream);

        result.payload = combinedStream;
      } else {
        result.payload = Binary.fromBuffer(
          Buffer.concat([ivBuffer, result.payload.asBuffer(), authTagBuffer])
        );
      }

      return result;
    });
};

/**
 * Process a GCM payload stream and returns it's constituent parts.
 * @param {Stream} payload - The paylaod as a stream
 * @returns {*} - encryption result
 * {
 *   payload: 'The payload part of the stream',
 *   payloadIv: 'The IV for the payload',
 *   payloadAuthTag: 'The authentication tag'
 * }
 */
AesGcmCipher.processGcmPayload = function (buffer) {
  // Read the 12 byte IV from the beginning of the stream
  const payloadIv = Binary.fromBuffer(buffer.slice(0, 12));

  // Slice the final 16 bytes of the buffer for the authentication tag
  const payloadAuthTag = Binary.fromBuffer(buffer.slice(-16));

  return {
    payload: Binary.fromBuffer(buffer.slice(12, -16)),
    payloadIv,
    payloadAuthTag,
  };
};

/**
 * Encrypts the payload using AES w/ CBC mode
 * @returns
 */
AesGcmCipher.prototype.decrypt = function (binary, key) {
  const { payload, payloadIv, payloadAuthTag } = AesGcmCipher.processGcmPayload(binary);

  return this.cryptoService.decrypt(
    payload,
    key,
    payloadIv,
    Algorithms.AES_256_GCM,
    payloadAuthTag
  );
};

module.exports = AesGcmCipher;
